import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./FormCSDatePicker.css";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { CalendarToday, Clear } from "@mui/icons-material";
import { format, isValid, parse } from "date-fns";
import { styleForTextField } from "../../../utils/TextField/TextField";
import { formatDateLocal } from "../../../../utils/hooks/dataHandler";

const DISPLAY_FORMAT = "yyyy-MM-dd";

export const parseFormCSIsoDate = (value) => {
  if (!value) return null;
  const parsed = parse(String(value), DISPLAY_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const isWithinRange = (date, minDate, maxDate) =>
  (!minDate || date >= minDate) && (!maxDate || date <= maxDate);

/**
 * Form C-S date field (IRAS YYYY-MM-DD). Standalone; matches FormCS input styling.
 */
const FormCSDatePicker = ({
  value = "",
  onChange,
  disabled = false,
  placeholder = "YYYY-MM-DD",
  minDate,
  maxDate,
  error = false,
  helperText = "",
}) => {
  const selected = parseFormCSIsoDate(value);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setInputValue(selected ? format(selected, DISPLAY_FORMAT) : "");
  }, [value]);

  const commitDate = (date) => {
    if (!date || !isValid(date)) {
      onChange("");
      setInputValue("");
      return;
    }
    if (!isWithinRange(date, minDate, maxDate)) return;
    onChange(formatDateLocal(date));
    setInputValue(format(date, DISPLAY_FORMAT));
  };

  const handlePickerChange = (date) => {
    commitDate(date);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    setInputValue(next);
    if (!next.trim()) {
      onChange("");
      return;
    }
    const parsed = parse(next, DISPLAY_FORMAT, new Date());
    if (isValid(parsed) && isWithinRange(parsed, minDate, maxDate)) {
      onChange(formatDateLocal(parsed));
    }
  };

  const handleBlur = () => {
    setInputValue(selected ? format(selected, DISPLAY_FORMAT) : "");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setInputValue("");
    setIsOpen(false);
  };

  const openCalendar = () => {
    if (!disabled) setIsOpen(true);
  };

  return (
    <Box className='form-cs-datepicker' sx={{ width: "100%" }}>
      <DatePicker
        wrapperClassName='form-cs-datepicker-wrapper'
        selected={selected}
        onChange={handlePickerChange}
        dateFormat={DISPLAY_FORMAT}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        open={isOpen}
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => setIsOpen(false)}
        popperPlacement='bottom-start'
        showMonthDropdown
        showYearDropdown
        dropdownMode='select'
        placeholderText={placeholder}
        customInput={
          <TextField
            size='small'
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onClick={openCalendar}
            disabled={disabled}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            autoComplete='off'
            inputProps={{
              autoComplete: "off",
              "aria-label": placeholder
            }}
            InputProps={{
              endAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  {Boolean(value) && !disabled && (
                    <Tooltip title='Clear date'>
                      <IconButton
                        size='small'
                        onClick={handleClear}
                        tabIndex={-1}
                        sx={{
                          color: "text.secondary",
                          "&:hover": { color: "error.main" }
                        }}
                      >
                        <Clear fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title='Open calendar'>
                    <IconButton
                      size='small'
                      onClick={openCalendar}
                      disabled={disabled}
                      tabIndex={-1}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" }
                      }}
                    >
                      <CalendarToday fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setIsOpen(false);
              if ((e.key === " " || e.key === "Enter") && !disabled) {
                e.preventDefault();
                setIsOpen(true);
              }
            }}
            sx={{
              ...styleForTextField(),
              width: "100%",
              "& .MuiOutlinedInput-root": {
                width: "100%",
              },
            }}
          />
        }
      />
    </Box>
  );
};

export default FormCSDatePicker;
